import { useEffect, useState, useCallback } from "react";
import api, { formatErr } from "../../../lib/api";
import { card, btn, btnGhost, label, inputStyle } from "../roads/_roadsCommon";

/** Ch 27.3 — Electrical Layout: distribution boards + load schedule + cable sizing. */
export default function ElectricalLayoutPanel({ projectId }) {
  const [model, setModel] = useState(null);
  const [palette, setPalette] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [err, setErr] = useState("");
  const [activePanel, setActivePanel] = useState(null);

  const reload = useCallback(async () => {
    try {
      const [m, p, s] = await Promise.all([
        api.get(`/projects/${projectId}/mep-re/model`),
        api.get(`/projects/${projectId}/mep-re/palette`),
        api.get(`/projects/${projectId}/mep-re/electrical/load-schedule`),
      ]);
      setModel(m.data.model);
      setPalette(p.data);
      setSchedule(s.data);
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  }, [projectId]);
  useEffect(() => { reload(); }, [reload]);

  const addPanel = async () => {
    const n = (model?.electrical_panels?.length || 0) + 1;
    await api.post(`/projects/${projectId}/mep-re/electrical/panels`,
      { tag: `DB-${n}`, level: "GF", incoming_length_m: 50, incoming_voltage_V: 415,
        loads: [
          { category: "lighting", connected_kW: 5, demand_factor: 0.66 },
          { category: "power-sockets", connected_kW: 10, demand_factor: 0.5 },
          { category: "ac-load", connected_kW: 20, demand_factor: 0.85 },
        ] });
    reload();
  };
  const delPanel = async (pid) => {
    await api.delete(`/projects/${projectId}/mep-re/electrical/panels/${pid}`);
    if (activePanel === pid) setActivePanel(null);
    reload();
  };

  const project = schedule?.project_summary;
  const panels = model?.electrical_panels || [];
  const active = panels.find(p => p.id === activePanel);

  return (
    <div data-testid="electrical-layout" style={{ display: "grid",
            gridTemplateColumns: "260px 1fr", gap: 12, padding: 16,
            height: "calc(100vh - 280px)", minHeight: 700 }}>
      {/* Load type catalogue */}
      <div style={{ ...card, padding: 12, overflowY: "auto" }}>
        <div style={label}>Load Categories (Demand Factor)</div>
        <table style={{ width: "100%", marginTop: 8, fontSize: 10 }}>
          <thead><tr><th style={{ textAlign: "left", color: "var(--pf-muted)" }}>Category</th>
            <th style={{ color: "var(--pf-muted)" }}>DF</th></tr></thead>
          <tbody>
            {(palette?.electrical_loads || []).map(l => (
              <tr key={l.id}>
                <td style={{ padding: 2 }}>{l.label}</td>
                <td style={{ padding: 2, textAlign: "center" }}>{l.demand_factor}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ ...label, marginTop: 14 }}>Project Summary</div>
        {project && (
          <div style={{ marginTop: 8, fontSize: 11, lineHeight: 1.6 }}>
            <div>Connected: <b>{project.total_connected_kW} kW</b></div>
            <div>Demand: <b>{project.total_demand_kW} kW</b></div>
            <div>kVA: <b>{project.total_demand_kVA}</b></div>
            <div>Transformer: <b>{project.transformer_kVA} kVA</b></div>
            <div>DG Set: <b>{project.DG_kVA} kVA</b></div>
          </div>
        )}
      </div>

      {/* Panels + active loads */}
      <div style={{ display: "grid", gridTemplateRows: "1fr 1fr", gap: 12, minHeight: 0 }}>
        <div style={{ ...card, padding: 12, overflowY: "auto" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={label}>Distribution Boards</div>
            <button data-testid="add-elec-panel" style={btn} onClick={addPanel}>+ Add Panel</button>
          </div>
          <table style={{ width: "100%", marginTop: 8, fontSize: 11 }}>
            <thead><tr style={{ borderBottom: "1px solid var(--pf-border)" }}>
              {["Tag","Level","Conn kW","Demand kW","kVA","Tx kVA","I (A)","Cable mm²","%Vd","Status",""]
                .map(h => <th key={h} style={{ textAlign: "left", padding: 4, color: "var(--pf-muted)" }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {panels.map(p => {
                const r = schedule?.panels?.find(x => x.panel_id === p.id) || {};
                const passing = r.vdrop_status === "PASS";
                return (
                  <tr key={p.id} data-testid={`elec-panel-${p.id}`}
                       onClick={() => setActivePanel(p.id)}
                       style={{ cursor: "pointer",
                                  background: activePanel === p.id ? "rgba(242,107,31,.1)" : "transparent" }}>
                    <td style={{ padding: 4, fontWeight: 600 }}>{p.tag}</td>
                    <td style={{ padding: 4 }}>{p.level}</td>
                    <td style={{ padding: 4 }}>{r.total_connected_kW ?? "—"}</td>
                    <td style={{ padding: 4 }}>{r.total_demand_kW ?? "—"}</td>
                    <td style={{ padding: 4 }}>{r.total_demand_kVA ?? "—"}</td>
                    <td style={{ padding: 4 }}>{r.transformer_kVA ?? "—"}</td>
                    <td style={{ padding: 4 }}>{r.incoming_current_A ?? "—"}</td>
                    <td style={{ padding: 4, fontWeight: 600 }}>{r.incoming_cable_mm2 ?? "—"}</td>
                    <td style={{ padding: 4 }}>{r.incoming_vdrop_pct ?? "—"}</td>
                    <td style={{ padding: 4, color: passing ? "#22c55e" : "#ef4444" }}>
                      {r.vdrop_status ?? "—"}
                    </td>
                    <td><button style={{ ...btnGhost, padding: "2px 6px", fontSize: 10 }}
                      onClick={(e) => { e.stopPropagation(); delPanel(p.id); }}>×</button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {/* Active panel loads */}
        <div style={{ ...card, padding: 12, overflowY: "auto" }}>
          <div style={label}>Sub-Circuit Loads {active ? `· ${active.tag}` : "· (select a panel)"}</div>
          {active ? (
            <table style={{ width: "100%", marginTop: 8, fontSize: 11 }}>
              <thead><tr style={{ borderBottom: "1px solid var(--pf-border)" }}>
                {["#","Category","Connected kW","DF","Demand kW"].map(h =>
                  <th key={h} style={{ textAlign: "left", padding: 4, color: "var(--pf-muted)" }}>{h}</th>)}
              </tr></thead>
              <tbody>
                {(active.loads || []).map((l, i) => (
                  <tr key={i}>
                    <td style={{ padding: 4 }}>{i + 1}</td>
                    <td style={{ padding: 4 }}>{l.category}</td>
                    <td style={{ padding: 4 }}>{l.connected_kW}</td>
                    <td style={{ padding: 4 }}>{l.demand_factor ?? 0.7}</td>
                    <td style={{ padding: 4, fontWeight: 600 }}>
                      {((l.connected_kW || 0) * (l.demand_factor ?? 0.7)).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div style={{ marginTop: 12, color: "var(--pf-muted)", fontSize: 11 }}>
              Click a row in the panels table to view its sub-circuits.
            </div>
          )}
        </div>
      </div>
      {err && <div style={{ position: "absolute", bottom: 8, color: "#FF8888" }}>{err}</div>}
    </div>
  );
}
